import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Save, Type, FileText, Layers, MapPin } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { festivalsAPI, circuitsAPI, venuesAPI, handleApiError } from '../utils/api';

const CreateEditFestivalPage = () => {
  const navigate = useNavigate();
  const { festivalId } = useParams();
  const { userRole } = useAuth();
  const isEditMode = !!festivalId;

  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    circuitId: '',
    venueId: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [circuits, setCircuits] = useState([]);
  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadCircuits = useCallback(async () => {
    try {
      const response = await circuitsAPI.getAll();
      setCircuits(response.data || []);
    } catch (err) {
      // Non bloquant : le festival peut être créé sans circuit
      console.error('Impossible de charger les circuits', err);
    }
  }, []);

  const loadVenues = useCallback(async () => {
    try {
      const response = await venuesAPI.getAll();
      setVenues(response.data || []);
    } catch (err) {
      // Non bloquant : le festival peut être créé sans lieu principal
      console.error('Impossible de charger les lieux', err);
    }
  }, []);

  const loadFestival = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await festivalsAPI.getById(festivalId);
      const festival = response.data;
      setFormData({
        title: festival.title || '',
        startDate: festival.startDate || '',
        endDate: festival.endDate || '',
        circuitId: festival.circuitId || festival.circuit?.id || '',
        venueId: festival.venueId || festival.venue?.id || '',
        description: festival.description || ''
      });
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setLoadError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [festivalId]);

  useEffect(() => {
    loadCircuits();
    loadVenues();
  }, [loadCircuits, loadVenues]);

  useEffect(() => {
    if (isEditMode) {
      loadFestival();
    }
  }, [isEditMode, loadFestival]);

  // Vérification admin - doit être après les hooks
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'La date de début est requise';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La date de fin est requise';
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'La date de fin doit être après la date de début';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: formData.title,
        startDate: formData.startDate,
        endDate: formData.endDate,
        description: formData.description,
        circuitId: formData.circuitId ? parseInt(formData.circuitId) : null,
        venueId: formData.venueId ? parseInt(formData.venueId) : null,
      };

      if (isEditMode) {
        await festivalsAPI.update(festivalId, payload);
      } else {
        await festivalsAPI.create(payload);
      }

      navigate('/manage-festivals');
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setErrors(prev => ({ ...prev, submit: `${errTitle} : ${details}` }));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement du festival...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/manage-festivals')}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à la gestion des festivals
          </button>

          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <Calendar className="mr-3" size={32} />
                {isEditMode ? 'Modifier le Festival' : 'Créer un Nouveau Festival'}
              </h1>
              <p className="mt-2 text-blue-100">
                {isEditMode ? 'Modifiez les informations du festival' : 'Remplissez les informations pour créer un festival'}
              </p>
            </div>
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {loadError}
            </div>
          )}

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {errors.submit}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
            <div className="space-y-6">
              {/* Titre */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <Type className="mr-2 text-blue-600" size={18} />
                  Titre du Festival *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex: Winamax Poker Tour Paris"
                  className={`w-full px-4 py-3 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="mr-2 text-blue-600" size={18} />
                    Date de début *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.startDate ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                </div>

                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="mr-2 text-blue-600" size={18} />
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.endDate ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="mr-2 text-blue-600" size={18} />
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez le festival..."
                  rows="4"
                  className={`w-full px-4 py-3 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>

              {/* Circuit */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <Layers className="mr-2 text-blue-600" size={18} />
                  Circuit (optionnel)
                </label>
                <select
                  name="circuitId"
                  value={formData.circuitId}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Aucun circuit</option>
                  {circuits.map((circuit) => (
                    <option key={circuit.id} value={circuit.id}>
                      {circuit.title} ({circuit.acronym})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  La couleur et le logo affichés pour ce festival proviennent du circuit sélectionné.
                </p>
              </div>

              {/* Lieu principal */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="mr-2 text-blue-600" size={18} />
                  Lieu principal (optionnel)
                </label>
                <select
                  name="venueId"
                  value={formData.venueId}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Aucun lieu</option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}{venue.city ? ` — ${venue.city}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Le lieu où se déroule l'essentiel du festival. Un tournoi individuel peut avoir son propre lieu.
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/manage-festivals')}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  disabled={isSaving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center disabled:opacity-50"
                >
                  <Save className="mr-2 flex-shrink-0" size={20} />
                  {isSaving ? 'Enregistrement...' : isEditMode ? (
                    <>
                      <span className="sm:hidden">Enregistrer</span>
                      <span className="hidden sm:inline">Enregistrer les modifications</span>
                    </>
                  ) : 'Créer le festival'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEditFestivalPage;
